/**
 * Products page content. Carries the structural data for each product —
 * id, image path, schedule, image-height override, and the spec ordering —
 * which components combine with the translated copy in `/src/locales/*`
 * at render time. All translatable strings (name, description, chips,
 * features, imageAlt, section titles + intros, **and spec values**) live
 * in the translation files; components look them up via `t()` keyed off
 * the product's `id`.
 *
 * Each `SpecCell` now carries only a `labelKey`. The label translates via
 * `products.specLabels.<labelKey>`, and the value translates via
 * `products.items.<productId>.specs.<labelKey>` — so embedded English
 * fragments (e.g. "lenses", "water content", "Light Blue") stay
 * localised per language rather than being hard-coded literals.
 */

export interface SpecCell {
  /** Lookup key under `products.specLabels.*` (label) and
   *  `products.items.<productId>.specs.*` (value). */
  labelKey: string;
}

export interface Product {
  id: string;
  name: string;
  schedule: 'Daily' | 'Monthly';
  image: string;
  /** Image wrapper height in px at xl+ (width is always 440). Defaults to 248. */
  imageHeight?: number;
  specs: readonly SpecCell[];
  availability?: readonly string[];
  unavailableIn?: readonly string[];
}

export interface SolutionProduct {
  id: string;
  name: string;
  image: string;
  imageHeight?: number;
  specs: readonly SpecCell[];
}

export interface ProductGroup {
  products: readonly Product[];
}

export interface ProductSection {
  id: string;
  /** Lookup key under `products.sections.<sectionKey>` and `products.heroLinks.<sectionKey>`. */
  sectionKey: 'clearFamily' | 'colorFamily' | 'standardFamily';
  groups: readonly ProductGroup[];
}

export interface SolutionSection {
  id: string;
  sectionKey: 'lensCare';
  product: SolutionProduct;
}

export function isLensProduct(p: Product | SolutionProduct): p is Product {
  return 'schedule' in p;
}

export const HERO_LINK_KEYS = [
  { key: 'clearFamily', hash: '#clear-family' },
  { key: 'colorFamily', hash: '#color-family' },
  { key: 'standardFamily', hash: '#standard-family' },
  { key: 'lensCare', hash: '#lens-care' },
] as const;

const CLEAR_1_DAY: Product = {
  id: 'clear-1-day',
  name: 'Clear 1-day',
  schedule: 'Daily',
  image: '/assets/Clear_1D_30.webp',
  specs: [
    { labelKey: 'standardPackSizes' },
    { labelKey: 'diameter' },
    { labelKey: 'nominalCentreThickness' },
    { labelKey: 'material' },
    { labelKey: 'dkt' },
    { labelKey: 'dkOxygen' },
    { labelKey: 'baseCurve' },
    { labelKey: 'powerRange' },
    { labelKey: 'handlingTint' },
  ],
  unavailableIn: ['US'],
};

const CLEAR_ALL_DAY: Product = {
  id: 'clear-all-day',
  name: 'Clear all-day',
  schedule: 'Monthly',
  image: '/assets/Clear_AllD.webp',
  imageHeight: 350,
  specs: [
    { labelKey: 'standardPackSizes' },
    { labelKey: 'diameter' },
    { labelKey: 'nominalCentreThickness' },
    { labelKey: 'material' },
    { labelKey: 'dkt' },
    { labelKey: 'dkOxygen' },
    { labelKey: 'baseCurve' },
    { labelKey: 'powerRange' },
    { labelKey: 'handlingTint' },
  ],
  unavailableIn: ['US'],
};

const CLEAR_1_DAY_TORIC: Product = {
  id: 'clear-1-day-toric',
  name: 'Clear 1-day toric',
  schedule: 'Daily',
  image: '/assets/Clear_1D_Toric.webp',
  specs: [
    { labelKey: 'standardPackSizes' },
    { labelKey: 'diameter' },
    { labelKey: 'nominalCentreThickness' },
    { labelKey: 'material' },
    { labelKey: 'dkt' },
    { labelKey: 'dkOxygen' },
    { labelKey: 'baseCurve' },
    { labelKey: 'powerRange' },
    { labelKey: 'cylinderPower' },
    { labelKey: 'axis' },
    { labelKey: 'handlingTint' },
  ],
  unavailableIn: ['US'],
};

const CLEAR_ALL_DAY_TORIC: Product = {
  id: 'clear-all-day-toric',
  name: 'Clear all-day toric',
  schedule: 'Monthly',
  image: '/assets/Clear_AllD_Toric.webp',
  imageHeight: 350,
  specs: [
    { labelKey: 'standardPackSizes' },
    { labelKey: 'diameter' },
    { labelKey: 'nominalCentreThickness' },
    { labelKey: 'material' },
    { labelKey: 'dkt' },
    { labelKey: 'dkOxygen' },
    { labelKey: 'baseCurve' },
    { labelKey: 'powerRange' },
    { labelKey: 'cylinderPower' },
    { labelKey: 'axis' },
    { labelKey: 'handlingTint' },
  ],
  unavailableIn: ['US'],
};

const CLEAR_38: Product = {
  id: 'clear-38',
  name: 'Clear 38',
  schedule: 'Monthly',
  image: '/assets/Clear_38.webp',
  imageHeight: 350,
  specs: [
    { labelKey: 'standardPackSizes' },
    { labelKey: 'diameter' },
    { labelKey: 'nominalCentreThickness' },
    { labelKey: 'material' },
    { labelKey: 'dkt' },
    { labelKey: 'dkOxygen' },
    { labelKey: 'baseCurve' },
    { labelKey: 'powerRange' },
    { labelKey: 'uvBlocking' },
  ],
  unavailableIn: ['US'],
};

const CLEAR_55A: Product = {
  id: 'clear-55a',
  name: 'Clear 55A',
  schedule: 'Monthly',
  image: '/assets/Clear_55.webp',
  imageHeight: 350,
  specs: [
    { labelKey: 'standardPackSizes' },
    { labelKey: 'diameter' },
    { labelKey: 'nominalCentreThickness' },
    { labelKey: 'material' },
    { labelKey: 'dkt' },
    { labelKey: 'dkOxygen' },
    { labelKey: 'baseCurve' },
    { labelKey: 'powerRange' },
    { labelKey: 'handlingTint' },
  ],
  unavailableIn: ['US'],
};

const CLEAR_55A_UV: Product = {
  id: 'clear-55a-uv',
  name: 'Clear 55A UV',
  schedule: 'Monthly',
  image: '/assets/Clear_55UV.webp',
  imageHeight: 350,
  specs: [
    { labelKey: 'standardPackSizes' },
    { labelKey: 'diameter' },
    { labelKey: 'nominalCentreThickness' },
    { labelKey: 'material' },
    { labelKey: 'dkt' },
    { labelKey: 'dkOxygen' },
    { labelKey: 'baseCurve' },
    { labelKey: 'powerRange' },
    { labelKey: 'uvBlocking' },
    { labelKey: 'handlingTint' },
  ],
  unavailableIn: ['US'],
};

const CLEAR_58: Product = {
  id: 'clear-58',
  name: 'Clear 58',
  schedule: 'Monthly',
  image: '/assets/Clear_58.webp',
  imageHeight: 350,
  specs: [
    { labelKey: 'standardPackSizes' },
    { labelKey: 'diameter' },
    { labelKey: 'nominalCentreThickness' },
    { labelKey: 'material' },
    { labelKey: 'dkt' },
    { labelKey: 'dkOxygen' },
    { labelKey: 'baseCurve' },
    { labelKey: 'powerRange' },
    { labelKey: 'uvBlocking' },
    { labelKey: 'handlingTint' },
  ],
  unavailableIn: ['US'],
};

const CLEARCOLOR_1_DAY: Product = {
  id: 'clearcolor-1-day',
  name: 'Clearcolor 1-day',
  schedule: 'Daily',
  image: '/assets/1.CC1D new box 30p (2).png',
  specs: [
    { labelKey: 'standardPackSizes' },
    { labelKey: 'diameter' },
    { labelKey: 'nominalCentreThickness' },
    { labelKey: 'material' },
    { labelKey: 'dkOxygen' },
    { labelKey: 'baseCurve' },
    { labelKey: 'powerRange' },
  ],
};

const CLEARCOLOR: Product = {
  id: 'clearcolor',
  name: 'Clearcolor',
  schedule: 'Monthly',
  image: '/assets/2.CC38 new box (3).png',
  specs: [
    { labelKey: 'standardPackSizes' },
    { labelKey: 'diameter' },
    { labelKey: 'nominalCentreThickness' },
    { labelKey: 'material' },
    { labelKey: 'dkOxygen' },
    { labelKey: 'baseCurve' },
    { labelKey: 'powerRange' },
  ],
};

const CLEARCOLOR_55: Product = {
  id: 'clearcolor-55',
  name: 'Clearcolor 55',
  schedule: 'Monthly',
  image: '/assets/3.CC55 new box (4).png',
  specs: [
    { labelKey: 'standardPackSizes' },
    { labelKey: 'diameter' },
    { labelKey: 'nominalCentreThickness' },
    { labelKey: 'material' },
    { labelKey: 'dkOxygen' },
    { labelKey: 'baseCurve' },
    { labelKey: 'powerRange' },
  ],
  unavailableIn: ['US'],
};

const PHANTOM_1_DAY: Product = {
  id: 'phantom-1-day',
  name: 'Phantom 1-day',
  schedule: 'Daily',
  image: '/assets/Phantom_1D%20copy.webp',
  specs: [
    { labelKey: 'standardPackSizes' },
    { labelKey: 'diameter' },
    { labelKey: 'material' },
    { labelKey: 'dkt' },
    { labelKey: 'dkOxygen' },
    { labelKey: 'baseCurve' },
    { labelKey: 'powerRange' },
  ],
};

const PHANTOM: Product = {
  id: 'phantom',
  name: 'Phantom',
  schedule: 'Monthly',
  image: '/assets/Phantom%20copy.webp',
  specs: [
    { labelKey: 'standardPackSizes' },
    { labelKey: 'diameter' },
    { labelKey: 'material' },
    { labelKey: 'dkt' },
    { labelKey: 'dkOxygen' },
    { labelKey: 'baseCurve' },
    { labelKey: 'powerRange' },
  ],
};

// US PRODUCTS

const EYEDIA_FRESH_DAILY_DISPOSABLE: Product = {
  id: 'eyedia-fresh-daily-disposable',
  name: 'Eyedia Fresh Daily Disposable',
  schedule: 'Daily',
  image: '/assets/fresh_1000.png',
  specs: [
    { labelKey: 'standardPackSizes' },
    { labelKey: 'diameter' },
    { labelKey: 'nominalCentreThickness' },
    { labelKey: 'material' },
    { labelKey: 'dkt' },
    { labelKey: 'dkOxygen' },
    { labelKey: 'baseCurve' },
    { labelKey: 'powerRange' },
    { labelKey: 'handlingTint' },
  ],
  availability: ['US'],
};

const EYEDIA_PRECISE_MONTHLY: Product = {
  id: 'eyedia-precise-monthly',
  name: 'Eyedia Precise Monthly',
  schedule: 'Monthly',
  image: '/assets/US eyedia_eyedia monthly.png',
  specs: [
    { labelKey: 'standardPackSizes' },
    { labelKey: 'diameter' },
    { labelKey: 'nominalCentreThickness' },
    { labelKey: 'material' },
    { labelKey: 'dkt' },
    { labelKey: 'dkOxygen' },
    { labelKey: 'baseCurve' },
    { labelKey: 'powerRange' },
    { labelKey: 'handlingTint' },
  ],
  availability: ['US'],
};

const EYEDIA_PRECISE_MONTHLY_ASTIGMATISM: Product = {
  id: 'eyedia-precise-monthly-astigmatism',
  name: 'Eyedia Precise Monthly Astigmatism',
  schedule: 'Monthly',
  image: '/assets/US eyedia_eyedia precise monthly astigmatism.png',
  specs: [
    { labelKey: 'standardPackSizes' },
    { labelKey: 'diameter' },
    { labelKey: 'nominalCentreThickness' },
    { labelKey: 'material' },
    { labelKey: 'dkt' },
    { labelKey: 'dkOxygen' },
    { labelKey: 'baseCurve' },
    { labelKey: 'powerRange' },
    { labelKey: 'handlingTint' },
  ],
  availability: ['US'],
};

const EYEDIA_SOFT_55: Product = {
  id: 'eyedia-soft-55',
  name: 'Eyedia Soft 55',
  schedule: 'Monthly',
  image: '/assets/US eyedia_eyedia soft 55.png',
  imageHeight: 350,
  specs: [
    { labelKey: 'standardPackSizes' },
    { labelKey: 'diameter' },
    { labelKey: 'nominalCentreThickness' },
    { labelKey: 'material' },
    { labelKey: 'dkt' },
    { labelKey: 'dkOxygen' },
    { labelKey: 'baseCurve' },
    { labelKey: 'powerRange' },
    { labelKey: 'uvBlocking' },
    { labelKey: 'handlingTint' },
  ],
  availability: ['US'],
};

const EYEDIA_SOFT_58: Product = {
  id: 'eyedia-soft-58',
  name: 'Eyedia Soft 58',
  schedule: 'Monthly',
  image: '/assets/US eyedia_eyedia soft 58.png',
  imageHeight: 350,
  specs: [
    { labelKey: 'standardPackSizes' },
    { labelKey: 'diameter' },
    { labelKey: 'nominalCentreThickness' },
    { labelKey: 'material' },
    { labelKey: 'dkt' },
    { labelKey: 'dkOxygen' },
    { labelKey: 'baseCurve' },
    { labelKey: 'powerRange' },
    { labelKey: 'handlingTint' },
  ],
  availability: ['US'],
};

const CUSTOM_LENS: Product = {
  id: 'custom-lens',
  name: 'Custom Lens',
  schedule: 'Monthly',
  image: '/assets/rx-medi-to-oder.png',
  imageHeight: 350,
  specs: [
    { labelKey: 'standardPackSizes' },
    { labelKey: 'diameter' },
    { labelKey: 'nominalCentreThickness' },
    { labelKey: 'material' },
    { labelKey: 'dkOxygen' },
    { labelKey: 'baseCurve' },
    { labelKey: 'powerRange' },
  ],
  availability: ['US'],
};

const EYEDIA: SolutionProduct = {
  id: 'eyedia',
  name: 'Eyedia',
  image: '/assets/Eyedia_360.webp',
  imageHeight: 410,
  specs: [{ labelKey: 'productContent' }, { labelKey: 'volume' }],
};

export const PRODUCT_SECTIONS: readonly ProductSection[] = [
  {
    id: 'clear-family',
    sectionKey: 'clearFamily',
    groups: [
      { products: [CLEAR_1_DAY, CLEAR_ALL_DAY] },
      { products: [CLEAR_1_DAY_TORIC, CLEAR_ALL_DAY_TORIC] },
      { products: [EYEDIA_FRESH_DAILY_DISPOSABLE] },
      { products: [EYEDIA_PRECISE_MONTHLY, EYEDIA_PRECISE_MONTHLY_ASTIGMATISM] },
    ],
  },
  {
    id: 'color-family',
    sectionKey: 'colorFamily',
    groups: [
      { products: [CLEARCOLOR_1_DAY, CLEARCOLOR, CLEARCOLOR_55] },
      { products: [PHANTOM_1_DAY, PHANTOM, CUSTOM_LENS] },
    ],
  },
  {
    id: 'standard-family',
    sectionKey: 'standardFamily',
    groups: [
      { products: [CLEAR_58, CLEAR_55A, CLEAR_55A_UV, CLEAR_38] },
      { products: [EYEDIA_SOFT_55, EYEDIA_SOFT_58] },
    ],
  },
] as const;

export const LENS_CARE_SECTION: SolutionSection = {
  id: 'lens-care',
  sectionKey: 'lensCare',
  product: EYEDIA,
};
